import { useState, useEffect, useCallback } from 'react'

const SIDEBAR_COLLAPSED_KEY = 'sn:sidebar-collapsed'
export const SIDEBAR_TOGGLE_EVENT = 'sn:sidebar-toggle'

/**
 * Dispatch a custom event when sidebar state changes
 * This allows other components to react to sidebar toggles
 */
export const dispatchSidebarToggle = (collapsed: boolean) => {
  window.dispatchEvent(
    new CustomEvent(SIDEBAR_TOGGLE_EVENT, { detail: { collapsed } })
  )
}

/**
 * Custom hook to manage sidebar collapsed state with localStorage persistence
 * and cross-component synchronization via custom events
 * @param defaultCollapsed - Default collapsed state (defaults to false)
 * @returns {[boolean, (collapsed: boolean) => void]} - [collapsed state, setter function]
 */
export const useSidebarState = (
  defaultCollapsed: boolean = false
): [boolean, (collapsed: boolean) => void] => {
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    try {
      const storedValue = localStorage.getItem(SIDEBAR_COLLAPSED_KEY)
      if (storedValue !== null) {
        return JSON.parse(storedValue)
      }
    } catch (error) {
      console.error('🚨 useSidebarState: Error reading from localStorage', {
        error,
      })
    }
    return defaultCollapsed
  })

  const setCollapsed = useCallback((newCollapsed: boolean) => {
    setCollapsedState(newCollapsed)
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(newCollapsed))
    } catch (error) {
      console.error('🚨 useSidebarState: Error saving to localStorage', {
        error,
      })
    }
    dispatchSidebarToggle(newCollapsed)
  }, [])

  // Listen for sidebar toggle events from other components (e.g., App.js)
  useEffect(() => {
    const handleSidebarToggle = (
      event: CustomEvent<{ collapsed: boolean }>
    ) => {
      setCollapsedState(event.detail.collapsed)
    }

    window.addEventListener(
      SIDEBAR_TOGGLE_EVENT,
      handleSidebarToggle as EventListener
    )

    return () => {
      window.removeEventListener(
        SIDEBAR_TOGGLE_EVENT,
        handleSidebarToggle as EventListener
      )
    }
  }, [])

  return [collapsed, setCollapsed]
}
